import { Table } from "antd";
import StatusTag from "../../../components/Tag/StatusTag";
import styles from "./DocsList.module.css";
import type { ColumnsType } from "antd/es/table";
import { CriteriaOption, Task } from "../../../types/task";
// import ProcessingStatusRow from "../../../components/TasksTable/TableColumns/CustomRows/Processing/ProcessingStatusRow";
import { sortTasksByDate } from "../../../utils/tasks";
import { getParsedFullDate } from "../../../utils/dateParsers";
import ActionRow from "../../../components/TasksTable/TableColumns/CustomRows/Completed/ActionRow";
import { criteriaLabelMap } from "../../../components/Forms/utils";
import { FileOutlined } from "@ant-design/icons";
import ValidationTag from "../../../components/Tag/ValidationTag";
import { useNavigate } from "react-router-dom";
import { useMessageContext } from "../../../providers";
import CriteriaTag from "../../../components/Tag/CriteriaTag";

const DocsList = ({ 
  tasks, 
  loading, 
  hasFilters = false 
}: { 
  tasks: Task[], 
  loading: boolean, 
  hasFilters?: boolean 
}) => {
  const navigate = useNavigate();
  const { sendMessage } = useMessageContext();

  const handleRowClick = (task: Task) => {
    if (task.status === "SUCCESS" && task.validated_at === null) {
      navigate(`/tasks/${task.id}`);
    } else if (task.status === "SUCCESS" && task.validated_at !== null) {
      sendMessage(
        `La tasca està validada. No es poden fer modificacions sobre el document ja que ha estat anonimitzat definitivament. Pots descarregar-lo fent clic al botó de Descarregar arxiu.`,
        "error",
        2.5
      );
    }
  };
  const columns: ColumnsType<Task> = [
    {
      title: "Nom de la tasca",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      //     <div style={{ padding: 8 }}>
      //         <Search
      //             placeholder="Buscar per nom"
      //             onSearch={() => confirm()}
      //             onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      //             value={selectedKeys[0] as string}
      //             style={{ marginBottom: 8, display: "block" }}
      //         />
      //     </div>
      // ),
      onFilter: (value, record) => record.name.toLowerCase().includes((value as string).toLowerCase()),
      ellipsis: true,
      width: 250,
    },
    {
      title: "Nom del document",
      dataIndex: "doc_name",
      key: "doc_name",
      sorter: (a, b) => a.doc_name.localeCompare(b.doc_name),
      // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      //     <div style={{ padding: 8 }}>
      //         <Search
      //             placeholder="Buscar per nom"
      //             onSearch={() => confirm()}
      //             onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      //             value={selectedKeys[0] as string}
      //             style={{ marginBottom: 8, display: "block" }}
      //         />
      //     </div>
      // ),
      onFilter: (value, record) => record.doc_name.toLowerCase().includes((value as string).toLowerCase()),
      width: 300,
      ellipsis: true,
    },
    {
      title: "Criteris",
      dataIndex: "criteria",
      key: "criteria",
      render: (criteria: CriteriaOption[]) => (
        <>
          {criteria?.map((criteri: CriteriaOption, index: number) => (
            <CriteriaTag key={criteri} criteria={criteri} />
          ))}
        </>
      ),
      sorter: (a, b) =>
        a.criteria?.join(", ").localeCompare(b.criteria?.join(", ")),
      // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      //   <div style={{ padding: 8 }}>
      //     <Search
      //       placeholder="Buscar per criteris"
      //       onSearch={() => confirm()}
      //       onChange={(e) =>
      //         setSelectedKeys(e.target.value ? [e.target.value] : [])
      //       }
      //       value={selectedKeys[0] as string}
      //       style={{ marginBottom: 8, display: "block" }}
      //     />
      //   </div>
      // ),
      onFilter: (value, record) =>
        record.criteria?.some((criteri: string) =>
          (criteriaLabelMap[criteri] || criteri)
            .toLowerCase()
            .includes((value as string).toLowerCase())
        ),
      width: 400,
      // width: 600,
    },
    {
      title: "Data de la tasca",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (
          <span>{getParsedFullDate(text, "·")}</span>
      ),
      sorter: (a, b) => sortTasksByDate(a.created_at, b.created_at),
      width: 200,
    },
    {
      title: "Estat de la tasca",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status, task) => {
          return <StatusTag status={status} error={task.error?.message}/>;
      },
      width: 180,
    },
    {
      title: "Validada",
      dataIndex: "validated_at",
      key: "validated_at",
      // sorter: (a, b) => a.validation.localeCompare(b.validation),
      render: (validated_at) => {
          return <ValidationTag validated_at={validated_at} />;
      },
      width: 150,
    },
    // {
    //     title: "Comentaris d'estat",
    //     dataIndex: "error",
    //     key: "error",
    //     render: (error, task) => <ProcessingStatusRow error={error} task={task} />,
    //     ellipsis: true,
    // },
    {
      title: "Accions",
      render: (_, task) => <ActionRow task={task} />,
      fixed: "right",
      width: 150,
    },
  ];

  const getEmptyMessage = () => {
    if (hasFilters) {
      return "No s'han trobat resultats. Prova ajustant els filtres o modificant el terme de cerca.";
    } else {
      return "La teva llista de tasques es troba buida. Pots afegir-les des de botó \"Crear tasca\".";
    }
  };

  const customLocale = {
    emptyText: (
      <div style={{ textAlign: "center", margin: 40 }} className={styles.emptyContainer}>
        <FileOutlined style={{ fontSize: 48, color: "#ccc", marginBottom: 15 }} className={styles.emptyIcon}/>
        <div>{getEmptyMessage()}</div>
      </div>
    ),
    triggerDesc: "Clic per ordenar descendent",
    triggerAsc: "Clic per ordenar ascendent",
    cancelSort: "Cancel·lar ordre",
  };

  return <Table 
    dataSource={tasks.filter((task) => !task.deleted_at).sort((a, b) => sortTasksByDate(b.created_at, a.created_at))} 
    columns={columns} 
    locale={customLocale} 
    pagination={{ pageSize: 8 }} 
    loading={loading}
    onRow={(record) => ({
      onClick: (event) => {
        const target = event.target as HTMLElement;
        if (target.closest('button') || target.closest('a') || target.closest('.ant-tooltip') || target.closest('.ant-modal')) {
          return;
        }
        handleRowClick(record);
      },
      style: (record.status === "SUCCESS" && record.validated_at === null) ? { cursor: 'pointer' } : { cursor: 'default' }
    })}
  />;
};

export default DocsList;